import { FC, useEffect, useState } from 'react'
import {
	Button,
	Card,
	Empty,
	Input,
	Pagination,
	Select,
	Spin,
	Tag
} from 'antd'
import {
	FileTextOutlined,
	PlayCircleOutlined,
	SearchOutlined,
	StarOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { learningStore } from '../store/learning'
import { authStore } from '../store/auth'
import { LearningCategory, LearningMaterial } from '../helpers/types/learning'
import { RNLearning, RNLearningAdmin, RNLearningFavorites } from '../helpers/routeNames'
import { PageBreadcrumb } from '../components/PageBreadcrumb'

const PAGE_SIZE = 12

export const Learning: FC = () => {
	const navigate = useNavigate()
	const { user } = authStore()
	const {
		materials,
		total,
		loading,
		categoryStats,
		fetchMaterials,
		fetchCategoryStats
	} = learningStore()

	const [page, setPage] = useState(1)
	const [search, setSearch] = useState('')
	const [category, setCategory] = useState<
		LearningCategory | undefined
	>()

	useEffect(() => {
		fetchCategoryStats()
	}, [fetchCategoryStats])

	useEffect(() => {
		fetchMaterials({
			page,
			limit: PAGE_SIZE,
			category,
			search: search || undefined
		})
	}, [page, category, search, fetchMaterials])

	const handleSearch = (value: string) => {
		setSearch(value)
		setPage(1)
	}

	const handleCategoryChange = (
		value: LearningCategory | undefined
	) => {
		setCategory(value)
		setPage(1)
	}

	return (
		<div style={{ padding: '16px', paddingBottom: '90px' }}>
			<PageBreadcrumb items={[{ label: 'База знаний' }]} />
			<header
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 16
				}}>
				<h1 style={{ margin: 0 }}>База знаний</h1>
				<div style={{ display: 'flex', gap: 8 }}>
					<Button
						icon={<StarOutlined />}
						onClick={() =>
							navigate(RNLearningFavorites)
						}>
						Избранное
					</Button>
					{user?.role === 'admin' && (
						<Button
							type="primary"
							onClick={() =>
								navigate(RNLearningAdmin)
							}>
							Управление
						</Button>
					)}
				</div>
			</header>

			<div
				style={{
					display: 'flex',
					gap: 12,
					marginBottom: 16,
					flexWrap: 'wrap'
				}}>
				<Input.Search
					placeholder="Поиск материалов..."
					allowClear
					onSearch={handleSearch}
					prefix={<SearchOutlined />}
					style={{ maxWidth: 320 }}
				/>
				<Select
					placeholder="Все категории"
					allowClear
					onChange={handleCategoryChange}
					style={{ minWidth: 200 }}
					value={category}>
					{categoryStats.map((cs) => (
						<Select.Option
							key={cs.category}
							value={cs.category}>
							{cs.category} ({cs.count})
						</Select.Option>
					))}
				</Select>
			</div>

			{loading ? (
				<div style={{ textAlign: 'center', padding: 60 }}>
					<Spin size="large" />
				</div>
			) : materials.length === 0 ? (
				<Empty description="Материалы не найдены" />
			) : (
				<>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns:
								'repeat(auto-fill, minmax(280px, 1fr))',
							gap: 16
						}}>
						{materials.map((m) => (
							<MaterialCard
								key={m.id}
								material={m}
								onClick={() =>
									navigate(
										`${RNLearning}/${m.id}`
									)
								}
							/>
						))}
					</div>

					{total > PAGE_SIZE && (
						<div
							style={{
								textAlign: 'center',
								marginTop: 24
							}}>
							<Pagination
								current={page}
								pageSize={PAGE_SIZE}
								total={total}
								onChange={setPage}
								showSizeChanger={false}
							/>
						</div>
					)}
				</>
			)}
		</div>
	)
}

const MaterialCard: FC<{
	material: LearningMaterial
	onClick: () => void
}> = ({ material, onClick }) => {
	return (
		<Card
			hoverable
			onClick={onClick}
			style={{ height: '100%' }}
			styles={{ body: { padding: 16 } }}>
			<h3
				style={{
					margin: '0 0 8px',
					fontSize: 16,
					lineHeight: 1.3
				}}>
				{material.title}
			</h3>
			{material.description && (
				<p
					style={{
						margin: '0 0 12px',
						color: '#666',
						fontSize: 13,
						display: '-webkit-box',
						WebkitLineClamp: 2,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden'
					}}>
					{material.description}
				</p>
			)}
			<div
				style={{
					display: 'flex',
					gap: 6,
					flexWrap: 'wrap',
					marginBottom: 8
				}}>
				<Tag color="blue">{material.category}</Tag>
				{material.relayTypeName && (
					<Tag>{material.relayTypeName}</Tag>
				)}
			</div>
			<div
				style={{
					display: 'flex',
					gap: 12,
					color: '#999',
					fontSize: 13
				}}>
				{!!material.pdfCount && (
					<span>
						<FileTextOutlined /> {material.pdfCount} PDF
					</span>
				)}
				{!!material.videoCount && (
					<span>
						<PlayCircleOutlined />{' '}
						{material.videoCount} видео
					</span>
				)}
			</div>
		</Card>
	)
}
